import React, { useEffect } from 'react';
import styled from 'styled-components';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { IntlShape, injectIntl } from 'react-intl';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardTitle from 'containers/common/Card/CardTitle';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import TablePagination from '@material-ui/core/TablePagination';

import { translate } from 'containers/services/translate';
import messages from 'containers/ValidatedClaimsPage/messages';
import { pushTo } from 'containers/services/routes-helper';
import { PAGES } from 'routes';
import {
  requestValidatedClaims,
  requestSearch,
} from 'containers/ValidatedClaimsPage/actions';
import {
  makeSelectCountValidatedClaims,
  makeSelectValidatedClaims,
} from 'containers/ValidatedClaimsPage/selectors';
import { ROW_PER_PAGE } from 'constants/global';
import { CLAIM_STATUS } from 'containers/data-structure';
import { makeSelectListingsByField } from 'containers/AppContainer/selectors';
import { ClaimByStatus, SagisConfig } from 'containers/AppContainer/typings';
import { formatClaim } from 'containers/AppContainer/utils';
import { makeSelectKeycloakToken } from 'containers/AppContainer/selectors';
import { LISTINGS } from 'containers/data-structure';
import { SEARCH_TYPE } from 'containers/ValidatedClaimsPage/constants';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import { getDate } from 'containers/ValidatedClaimsPage/utils';
import NewSearchBar, { SearchQuery } from 'components/NewSearchBar';
import { SEARCH_QUERY_INIT_VALUES } from 'components/NewSearchBar/constants';

const Container = styled.div`
   {
    margin-top: 50px;
  }
  .title {
    text-align: left;
    font-family: Roboto;
    font-weight: Bold;
    font-size: 18px;
    letter-spacing: 0;
    color: #384264;
    opacity: 1;
    padding: 10px;
  }
  .headerTitle {
    text-align: left;
    font-size: 11px;
    letter-spacing: 0;
    color: #000000de;
    text-transform: none;
    opacity: 1;
    font-family: Roboto;
    padding-bottom: 0px;
    font-weight: Bold;
  }
  .row {
    border-radius: 4px;
    text-align: left;
    font-family: Roboto;
    letter-spacing: 0;
    font-size: 11px;
    color: #2b2727;
  }
  .claims {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
  }
  .pendingClaims {
    padding-top: 32px;
    width: 100%;
  }
  .header {
    display: flex;
    justify-content: space-between;
  }
`;

export interface ValidatedClaimsPageProps {
  intl: IntlShape;
  requestValidatedClaims: Function;
  countClaims: number;
  validatedClaims: ClaimByStatus[];
  cities: SagisConfig[];
  keycloakToken: string | null;
  requestSearch: Function;
  location: { pathname: string };
}

export const ValidatedClaimsPage: React.FC<ValidatedClaimsPageProps> = (
  props,
): JSX.Element => {
  const [page, setPage] = React.useState(0);
  const [pageSize, setRowsPerPage] = React.useState(25);
  const [searchQuery, setSearchQuery] = React.useState<SearchQuery>(
    SEARCH_QUERY_INIT_VALUES,
  );

  useEffect(() => {
    if (props.keycloakToken)
      props.requestValidatedClaims(CLAIM_STATUS.VALIDATED, page, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.keycloakToken]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    props.requestValidatedClaims(CLAIM_STATUS.VALIDATED, newPage, pageSize);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const size = parseInt(event.target.value, 10);
    setRowsPerPage(size);
    props.requestValidatedClaims(CLAIM_STATUS.VALIDATED, page, size);
  };

  const formattedValidatedClaims = formatClaim(
    props.validatedClaims,
    props.cities as SagisConfig[],
  );

  const handlerSearch = () => {
    const query = {
      searchCriteria: {
        ...searchQuery,
        status: CLAIM_STATUS.VALIDATED,
      },
      searchType: SEARCH_TYPE.CLAIM,
    };
    props.requestSearch(query, props.keycloakToken);
  };

  const handlerRefresh = () => {
    setSearchQuery(SEARCH_QUERY_INIT_VALUES);
    props.requestValidatedClaims(CLAIM_STATUS.VALIDATED, page, pageSize);
  };

  return (
    <Container>
      <Card>
        <CardContent>
          <div className="header">
            <CardTitle
              count={props.countClaims}
              message={translate({ ...messages.title }, props)}
            />
            {featureService.hasFeature(FEATURES.SEARCH_BAR) && (
              <NewSearchBar
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                handlerSearch={handlerSearch}
                handlerRefresh={handlerRefresh}
                isHomePage={false}
                showAll={true}
              />
            )}
          </div>
          <div>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="left" className="headerTitle">
                    {translate({ ...messages.claimNumber }, props)}
                  </TableCell>
                  <TableCell align="left" className="headerTitle">
                    {translate({ ...messages.registrationNumber }, props)}
                  </TableCell>
                  <TableCell align="left" className="headerTitle">
                    {translate({ ...messages.openingDate }, props)}
                  </TableCell>
                  <TableCell align="left" className="headerTitle">
                    {translate({ ...messages.lossDate }, props)}
                  </TableCell>
                  <TableCell align="left" className="headerTitle">
                    {translate({ ...messages.claimPlace }, props)}
                  </TableCell>
                  <TableCell align="left" className="headerTitle">
                    {translate({ ...messages.lossHour }, props)}
                  </TableCell>
                  <TableCell align="left" className="headerTitle">
                    {translate({ ...messages.numberOfVictim }, props)}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {formattedValidatedClaims.map((row: any) => (
                  <TableRow
                    key={row.id}
                    onClick={pushTo(`${PAGES.RECAP}/${row.id}`)}
                  >
                    <TableCell align="left" className="row">
                      {row.claimNumber}
                    </TableCell>
                    <TableCell align="left" className="row">
                      {row.registrationNumber}
                    </TableCell>
                    <TableCell align="left" className="row">
                      {getDate(row.createdAt)}
                    </TableCell>
                    <TableCell align="left" className="row">
                      {getDate(row.lossDate)}
                    </TableCell>
                    <TableCell align="left" className="row">
                      {row.lossLocationLabel}
                    </TableCell>
                    <TableCell align="left" className="row">
                      {row.lossHour}
                    </TableCell>
                    <TableCell align="left" className="row">
                      {row.victims.length}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={ROW_PER_PAGE}
              component="div"
              count={props.countClaims}
              rowsPerPage={pageSize}
              page={page}
              labelRowsPerPage={translate({ ...messages.linesPerPage }, props)}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </div>
        </CardContent>
      </Card>
    </Container>
  );
};
const mapDispatchToProps = {
  requestValidatedClaims,
  requestSearch,
};

const mapStateToProps = createStructuredSelector({
  validatedClaims: makeSelectValidatedClaims(),
  countClaims: makeSelectCountValidatedClaims(),
  cities: makeSelectListingsByField(LISTINGS.CITIES),
  keycloakToken: makeSelectKeycloakToken(),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(injectIntl(ValidatedClaimsPage));
